// sessionFormat.ts — the Sessions page's display strings and colors, kept
// out of the components so each can be tested without rendering one.
//
//   formatUSD / formatWh  money and energy at a precision that suits the
//                         magnitude (a conversation usually costs a small
//                         fraction of a cent).
//   describeCost          the cost badge's figure and tooltip, or null when
//                         the figure is unknown.
//   hueFromString         a stable per-session hue for the sidebar rows.
//   rowAccent             the row's identity hue.
//   turnInFlight          whether a session counts as running (green dot).
//   rowSubtitle           what a sidebar row says it is doing (its tooltip).
//   rowTooltip            the hover text a one-line sidebar row carries.
//   rowAriaLabel          the row's accessible name: title plus recency.
//   titleToCommit         whether a title edit should be sent as a rename.
//   sendButtonTitle       the composer's Send tooltip for the session state.
//
// Pure: no React, no DOM, no network.

import type { SessionCost } from '../api';

// formatUSD picks precision from magnitude. A conversation's electricity
// cost is usually a small fraction of a cent, and rounding everything to
// two decimals would render every session as "$0.00" — which reads as
// free rather than as cheap.
export function formatUSD(v: number): string {
  if (!Number.isFinite(v) || v === 0) return '$0';
  if (Math.abs(v) >= 1) return `$${v.toFixed(2)}`;
  if (Math.abs(v) >= 0.01) return `$${v.toFixed(4)}`;
  if (Math.abs(v) < 0.000001) return '<$0.000001';
  return `$${v.toFixed(6)}`;
}

export function formatWh(wh: number): string {
  if (!Number.isFinite(wh) || wh === 0) return '0 Wh';
  if (wh >= 1000) return `${(wh / 1000).toFixed(2)} kWh`;
  if (wh >= 1) return `${wh.toFixed(2)} Wh`;
  return `${(wh * 1000).toFixed(0)} mWh`;
}

export interface CostDescription {
  /** The badge text, prefixed with "≥" when some turns went unmetered. */
  label: string;
  /** The breakdown, for the badge's tooltip. */
  title: string;
}

// describeCost is null when the cost is unknown — no metered request
// stored yet, or a metering outage — because "$0.00" would claim the
// conversation was free rather than admit we don't know.
export function describeCost(cost: SessionCost | null): CostDescription | null {
  if (!cost?.available || cost.cost_usd === undefined) return null;

  const tokens = (cost.prompt_tokens ?? 0) + (cost.completion_tokens ?? 0);
  const parts = [
    `${cost.requests ?? 0} request${cost.requests === 1 ? '' : 's'}`,
    `${tokens.toLocaleString()} tokens`,
    `${formatWh(cost.energy_wh ?? 0)} of GPU energy`,
  ];
  if (cost.provider_cost_usd) {
    parts.push(`${formatUSD(cost.provider_cost_usd)} billed by an external provider`);
  }
  if (cost.partial) {
    parts.push('some turns went unmetered, so this is a lower bound');
  }
  if (cost.stale) {
    parts.push('inference could not be reached just now, so the newest requests may be missing');
  }
  return {
    label: `${cost.partial ? '≥' : ''}${formatUSD(cost.cost_usd)}`,
    title: `Session cost — ${parts.join(' · ')}. Priced from GPU energy measured while your turns ran.`,
  };
}

// hueFromString picks a deterministic hue 0-359 from a string so the
// outline button colors are stable across reloads. Matches the approach
// used by increment's RepoGraphView.
export function hueFromString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % 360;
}

// rowAccent hashes the session's immutable id rather than its name: an
// auto-generated title changes every turn, which would re-hue the row on
// every refresh. It is the row's label color and outline; ButtonBadge
// derives the unselected outline from it at reduced strength, since with
// the fill gone (C-MSG-1) the outline is what marks selection.
export function rowAccent(sessionId: string): string {
  return `hsl(${hueFromString(sessionId)} 70% 60%)`;
}

// turnInFlight is what "running" means in the sidebar: the harness says a
// turn is running or being cancelled. A ready-but-idle Pod is not running
// in this sense — every session on a live workspace would light up.
export function turnInFlight(status: string): boolean {
  return status === 'running' || status === 'cancelling';
}

export interface RowSubtitleState {
  /** A turn is running or cancelling in this session. */
  working: boolean;
  /** The harness's live label for that turn, when it has one. */
  note: string;
  /** The session's pod phase from the manager socket, if known. */
  phase?: 'provisioning' | 'ready' | 'suspended';
  /** The stored, server-generated subtitle. */
  subtitle: string;
}

// rowSubtitle: while a turn runs, what it is doing outranks the stored
// subtitle — the subtitle describes the last turn, and the row is about
// this one. A session whose Pod is down says so instead, since that is
// why it looks idle. Empty when there is nothing to say: it is hover
// text now, not a line whose height has to be reserved.
export function rowSubtitle(s: RowSubtitleState): string {
  if (s.working) return s.note || 'thinking…';
  if (s.phase === 'suspended') return 'suspended';
  if (s.phase === 'provisioning') return 'starting…';
  return s.subtitle;
}

export interface RowTooltipState {
  /** The full title, which the row itself ellipsizes. */
  title: string;
  /** rowSubtitle's line. */
  subtitle: string;
  /** The recency timestamp in ms, 0 when unknown. */
  stamp: number;
  /** The user named the session, so turns won't re-title it. */
  pinned: boolean;
  /** Renders the timestamp; injected so tests don't depend on the locale. */
  formatStamp?: (ms: number) => string;
}

// rowTooltip is everything a sidebar row stopped printing when it went to
// one line: the full title (the row ellipsizes it), what the session is
// doing, and the exact time the badge rounds. One fact per line.
export function rowTooltip(t: RowTooltipState): string {
  const fmt = t.formatStamp ?? ((ms: number) => new Date(ms).toLocaleString());
  const lines = [t.pinned ? `${t.title} (named by you)` : t.title];
  if (t.subtitle.trim()) lines.push(t.subtitle);
  if (t.stamp) lines.push(`Last message ${fmt(t.stamp)}`);
  return lines.join('\n');
}

// rowAriaLabel: the row's accessible name, since its visual lead is a dot
// or a bare "3d". A count reads as an age ("3d ago"); a date is spoken as
// printed ("last message 25 Aug"). A fork names its parent, since the
// indent that shows the nesting is visual only.
export function rowAriaLabel(
  title: string,
  working: boolean,
  recency: string,
  forkOf?: string,
): string {
  const name = forkOf ? `${title}, fork of ${forkOf}` : title;
  if (working) return `${name}, running`;
  if (!recency) return name;
  if (/^\d+[smhd]$/.test(recency)) return `${name}, ${recency} ago`;
  return `${name}, last message ${recency}`;
}

// titleToCommit returns the draft to send as a rename, or null when the
// edit should be dropped: no edit at all, an empty one, or one that only
// re-typed the stored name. The last case matters — clicking through a
// row must not pin its auto-title.
export function titleToCommit(draft: string | undefined, stored: string): string | null {
  if (draft === undefined) return null;
  const trimmed = draft.trim();
  if (!trimmed || trimmed === stored) return null;
  return draft;
}

// sendButtonTitle says what pressing Send will do in the session's state:
// steer a running turn, queue for a pod that isn't up, or send.
export function sendButtonTitle(streaming: boolean, phase: string | null): string {
  if (streaming) return 'Add this to the running turn';
  if (phase !== 'ready') return 'Send when the session is ready';
  return 'Send';
}
